import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  listGiraAmateurEtapasByGira,
  listPreciosAmateur,
  getDescuentoCutoffEndOfDayFromEtapaDocs,
  getDefaultDescuentoCutoffEndOfDay,
  getEventualCupoForEtapa
} from '../../lib/appwrite';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 600 },
  maxWidth: 600,
  maxHeight: { xs: '90vh', sm: '80vh' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  overflow: 'auto'
};

// Devuelve la etapa cuya AmaEtapaFInicio (o AmaGiraEtapaFInicio) es la fecha más próxima que aún no ha llegado.
// etapas: array de filas de etapas (selectedGira.etapas).
// Retorna { numero: código de etapa } o { numero: 0 } si no hay ninguna etapa futura.
function getNextUpcomingEtapa(etapas) {
  if (!etapas || !Array.isArray(etapas) || etapas.length === 0) return { numero: 0 };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const withDate = etapas
    .map((row, i) => {
      const fInicio = row.AmaEtapaFInicio ?? row.AmaGiraEtapaFInicio ?? row.FInicio ?? row.AmaGiraFInicio ?? row.FechaInicio;
      if (fInicio == null) return null;
      const inicio = new Date(fInicio);
      inicio.setHours(0, 0, 0, 0);
      const codigo = row.AmaGiraEtapaCodigo ?? row.AmaEtapaCodigo ?? row.EtapaCodigo ?? (i + 1);
      const numero = typeof codigo === 'number' ? codigo : parseInt(codigo, 10) || (i + 1);
      return { inicio, numero };
    })
    .filter((x) => x && x.inicio > today);
  if (withDate.length === 0) return { numero: 0 };
  withDate.sort((a, b) => a.inicio - b.inicio);
  return { numero: withDate[0].numero };
}

const GiraAmaRegistration = ({ 
  open, 
  onClose, 
  selectedGira,
  amateurData,
  clubes = [],
  user,
  onSave,
  isSaving = false,
  isReferenciaEspecialEligible = false,
  onRegisterAmateurProfile
}) => {
  const [localIsSaving, setLocalIsSaving] = useState(false);
  const [isEventual, setIsEventual] = useState(false);
  const [codigoReferenciaEspecial, setCodigoReferenciaEspecial] = useState('');
  const [eventualHasCupo, setEventualHasCupo] = useState(true);
  const [eventualCheckLoading, setEventualCheckLoading] = useState(false);
  const submittingRef = useRef(false);

  const eventualEtapa = selectedGira?.etapas
    ? getNextUpcomingEtapa(selectedGira.etapas).numero
    : 0;
  const isEventualBlocked = isEventual && (eventualCheckLoading || !eventualHasCupo);

  // Reset local saving state and ref when parent isSaving changes (cuando termina el guardado)
  useEffect(() => {
    if (!isSaving) {
      submittingRef.current = false;
      setLocalIsSaving(false);
    }
  }, [isSaving]);

  const handleEventualChange = (event) => {
    setIsEventual(event.target.checked);
  };

  useEffect(() => {
    if (!isEventual) {
      setEventualHasCupo(true);
      setEventualCheckLoading(false);
      return;
    }
    if (!selectedGira?.gira || !eventualEtapa) {
      setEventualHasCupo(false);
      return;
    }
    let cancelled = false;
    setEventualCheckLoading(true);
    getEventualCupoForEtapa(selectedGira.gira, eventualEtapa)
      .then((cupo) => {
        if (!cancelled) setEventualHasCupo(cupo.disponible === true);
      })
      .catch(() => {
        if (!cancelled) setEventualHasCupo(false);
      })
      .finally(() => {
        if (!cancelled) setEventualCheckLoading(false);
      });
    return () => { cancelled = true; };
  }, [isEventual, selectedGira?.gira, eventualEtapa]);

  // Etapa vigente por fechas (en base al array selectedGira.etapas)
  const getCurrentEtapaByDate = (etapas) => {
    if (!etapas || !Array.isArray(etapas) || etapas.length === 0) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < etapas.length; i++) {
      const row = etapas[i];
      const fInicio = row.AmaEtapaFInicio ?? row.AmaGiraEtapaFInicio ?? row.FInicio ?? row.AmaGiraFInicio ?? row.FechaInicio;
      const fFin = row.AmaEtapaFFin ?? row.AmaGiraEtapaFFin ?? row.FFin ?? row.AmaGiraFFin ?? row.FechaFin;
      if (fInicio != null && fFin != null) {
        const inicio = new Date(fInicio);
        const fin = new Date(fFin);
        inicio.setHours(0, 0, 0, 0);
        fin.setHours(23, 59, 59, 999);
        if (today >= inicio && today <= fin) {
          const codigo = row.AmaGiraEtapaCodigo ?? row.EtapaCodigo ?? (i + 1);
          const n = typeof codigo === 'number' ? codigo : parseInt(codigo, 10);
          return Number.isFinite(n) ? n : (i + 1);
        }
      }
    }
    return 0;
  };

  const getLastEtapaNumber = (etapas) => {
    if (!etapas || !Array.isArray(etapas) || etapas.length === 0) return 0;
    return etapas.reduce((max, row, idx) => {
      const codigo = row.AmaGiraEtapaCodigo ?? row.EtapaCodigo ?? (idx + 1);
      const n = typeof codigo === 'number' ? codigo : parseInt(codigo, 10) || 0;
      return n > max ? n : max;
    }, 0);
  };

  const pickPrecio = (preciosDocs, descripcionExacta) => {
    const key = String(descripcionExacta).trim().toLowerCase();
    const doc = (preciosDocs || []).find((d) => String(d.Descripcion || '').trim().toLowerCase() === key);
    if (!doc) return null;
    return { precio: Number(doc.precio) || 0, clip: doc.clip ?? null };
  };

  const computeAmaInsPrice = async ({ giraCodigo, etapaValue, isEventualLocal, clubcodigo }) => {
    // Igual que saveGiraInscripcionAmateur (backend): jugador con club ≠ 0 y ≠ 99 = tarifa afiliado.
    // No usar lista `clubes`: si aún no cargó, antes daba isAsociado=false y cobraba como sin club.
    const cc = Number(clubcodigo);
    const isAsociado = cc !== 0 && Number.isFinite(cc) && cc !== 99;

    const precios = await listPreciosAmateur();

    // Eventual: depende de club sede por etapa (Appwrite giraamateur)
    if (isEventualLocal) {
      const etapaDocs = await listGiraAmateurEtapasByGira(giraCodigo);
      const etapaDoc = (etapaDocs || []).find((d) => Number(d.Etapa) === Number(etapaValue));
      const clubSede = etapaDoc?.Club != null ? Number(etapaDoc.Club) : null;
      const isClubSede = clubSede != null ? Number(clubSede) === Number(clubcodigo) : false;

      if (isAsociado && isClubSede) return pickPrecio(precios, 'Eventual afiliado al club sede');
      if (isAsociado && !isClubSede) return pickPrecio(precios, 'Precio Eventual No afiliado al club sede (Afiliado)');
      return pickPrecio(precios, 'Eventual no afiliado');
    }

    // Gira completa: última etapa o corte de precio "descuento" (Appwrite Descuento en 1.ª etapa; si no, 16 abr)
    const currentEtapa = getCurrentEtapaByDate(selectedGira?.etapas);
    const lastEtapa = getLastEtapaNumber(selectedGira?.etapas);
    const isUltimaEtapa = currentEtapa > 0 && lastEtapa > 0 && currentEtapa === lastEtapa;

    if (isUltimaEtapa) {
      return pickPrecio(precios, isAsociado ? 'Precio Afiliado ultima etapa' : 'Precio NO afiliado ultima etapa');
    }

    const etapaDocsForDescuento = await listGiraAmateurEtapasByGira(giraCodigo);
    const today = new Date();
    const cutoff =
      getDescuentoCutoffEndOfDayFromEtapaDocs(etapaDocsForDescuento) ??
      getDefaultDescuentoCutoffEndOfDay(today);
    const isEnPeriodoDescuento = today <= cutoff;

    if (isEnPeriodoDescuento) {
      return pickPrecio(precios, isAsociado ? 'Precio Afiliado antes del 31 de Marzo' : 'Precio NO Afiliado antes del 31 de Marzo');
    }
    return pickPrecio(precios, isAsociado ? 'Precio Afiliado despues del 31 de Marzo' : 'Precio NO Afiliado despues del 31 de Marzo');
  };

  const handleSave = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    if (isSaving || localIsSaving) {
      submittingRef.current = false;
      return;
    }
    if (isEventual && isEventualBlocked) {
      submittingRef.current = false;
      return;
    }
    if (!amateurData || !selectedGira || !user?.email) {
      submittingRef.current = false;
      console.error('Missing required data for registration');
      return;
    }
    const amateurCodigo = amateurData[0]?.AmateurCodigo;
    if (amateurCodigo == null || amateurCodigo === '') {
      submittingRef.current = false;
      console.error('Missing AmateurCodigo for registration');
      return;
    }
    setLocalIsSaving(true);

    // Etapa: si es eventual, la etapa cuya AmaEtapaFInicio es la más próxima por venir; si no, 0 (gira completa).
    const nextEtapa = getNextUpcomingEtapa(selectedGira.etapas);
    const etapaValue = isEventual ? nextEtapa.numero : 0;

    if (isEventual) {
      try {
        const cupo = await getEventualCupoForEtapa(selectedGira.gira, etapaValue);
        if (!cupo.disponible) {
          submittingRef.current = false;
          setLocalIsSaving(false);
          return;
        }
      } catch {
        submittingRef.current = false;
        setLocalIsSaving(false);
        return;
      }
    }

    // Precio (Appwrite): afiliado según clubcodigo del perfil; sede de etapa desde Appwrite giraamateur (eventual)
    let amaInsImporte = null;
    try {
      const clubcodigo = amateurData?.[0]?.clubcodigo ?? amateurData?.[0]?.club ?? 0;
      const price = await computeAmaInsPrice({
        giraCodigo: selectedGira.gira,
        etapaValue,
        isEventualLocal: isEventual,
        clubcodigo,
      });
      if (price) amaInsImporte = price.precio;
    } catch (e) {
      console.warn('No se pudo calcular precio con Appwrite, el backend usará fallback.', e);
    }

    // Solo IDs y números: el backend carga el jugador por amateurCodigo/email (no reenviar SELECT *).
    const registrationData = {
      giracodigo: selectedGira.gira,
      email: user.email,
      amateurCodigo,
      isEventual,
      etapa: etapaValue,
      amaInsImporte,
      amaInsTicketText: !isEventual && isReferenciaEspecialEligible && codigoReferenciaEspecial
        ? codigoReferenciaEspecial.trim()
        : null
    };

    if (onSave) {
      onSave(registrationData);
    } else {
      console.warn('onSave function not provided');
      submittingRef.current = false;
      setLocalIsSaving(false);
    }
  };

  // Check if user has amateur profile
  const hasAmateurProfile = amateurData && amateurData.length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="gira-ama-modal-title"
    >
      <Box sx={style}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="gira-ama-modal-title" variant="h6" component="h2">
            Inscripción a Gira Amateur
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {selectedGira && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Gira Information */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 2,
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              {selectedGira.foto && (
                <Box
                  component="img"
                  src={selectedGira.foto}
                  alt={`Logo ${selectedGira.nombre}`}
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    objectFit: 'contain',
                    borderRadius: 2,
                    flexShrink: 0,
                    display: { xs: 'none', sm: 'block' }
                  }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {selectedGira.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${new Date(selectedGira.inicio).toLocaleDateString()} - ${new Date(selectedGira.fin).toLocaleDateString()}`}
                </Typography>
              </Box>
            </Box>

            {/* Amateur Profile Check */}
            {!hasAmateurProfile ? (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 7,
                  cursor: onRegisterAmateurProfile ? 'pointer' : 'default',
                  alignItems: 'center',
                  '& .MuiAlert-icon': {
                    alignSelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    p: 0,
                    fontSize: '1.5rem',
                  },
                  '& .MuiAlert-message': {
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    py: 0.25,
                  },
                  ...(onRegisterAmateurProfile && {
                    '&:hover': {
                      backgroundColor: 'warning.light',
                    }
                  })
                }}
                onClick={() => onRegisterAmateurProfile?.()}
                onKeyDown={(e) => {
                  if (onRegisterAmateurProfile && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onRegisterAmateurProfile();
                  }
                }}
                tabIndex={onRegisterAmateurProfile ? 0 : undefined}
                role={onRegisterAmateurProfile ? 'button' : undefined}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Registrar como Jugador Amateur
                </Typography>
                <Typography variant="body2">
                  Necesitas registrar tu perfil como jugador amateur para poder participar. Click para registrarte.
                </Typography>
              </Alert>
            ) : (
              <Box sx={{ 
                p: 2, 
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 2,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium', mb: 1 }}>
                  Perfil Amateur Verificado
                </Typography>
                {amateurData[0] && (
                  <Typography variant="body2" color="text.secondary">
                    {`${amateurData[0].AmateurNombre || ''} ${amateurData[0].AmateurApellidos || ''}`.trim()}
                  </Typography>
                )}
              </Box>
            )}

            {/* Registration Form */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEventual}
                      onChange={handleEventualChange}
                      color="primary"
                      disabled={!eventualEtapa}
                    />
                  }
                  label="Eventual (Etapa vigente únicamente)"
                />
                {isEventual && (
                  <Typography variant="body2" color={isEventualBlocked ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                    {eventualCheckLoading
                      ? 'Validando cupos...'
                      : (isEventualBlocked
                        ? 'No hay lugares de eventuales disponibles para esta etapa.'
                        : '')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Al confirmar, se realizará tu inscripción a esta gira amateur.
                </Typography>
              </Grid>
              {isReferenciaEspecialEligible && !isEventual && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Codigo de referencia
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ingresa el código de referencia"
                      value={codigoReferenciaEspecial}
                      onChange={(e) => setCodigoReferenciaEspecial(e.target.value)}
                      inputProps={{ maxLength: 50 }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end', 
              gap: 2, 
              mt: 2 
            }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  order: { xs: 2, sm: 1 }
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!hasAmateurProfile || isSaving || localIsSaving || isEventualBlocked}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  order: { xs: 1, sm: 2 }
                }}
                onMouseDown={(e) => {
                  if (isSaving || localIsSaving) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                {(isSaving || localIsSaving) ? 'Guardando...' : 'Confirmar Inscripción'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default GiraAmaRegistration;
